import { useQueryClient, useQuery } from "@tanstack/react-query";
import { notificacionError, notificacion, notificacionTipo } from "../../../../components/Alerts";
import { BaseModal, ModalCloseCross } from "../../../../components/modals/BaseModal";
import { ComunicacionAjax } from "../../../../ts/api/comunicacion";
import type { Usuario, Respuesta } from "../../../../ts/typings/clases";
import { useEffect, useRef, useState } from "react";
import { useDisclosure } from "../../../../hooks/UseDisclosure";
import { QueryError } from "../../../../components/Loading/Loading";

interface ModalEditarUsuarioProps {
	readonly shown: boolean;
	readonly close: () => void;
	readonly usuario: Usuario | null;
}
export function ModalEditarUsuario(props: ModalEditarUsuarioProps) {
	const fallbackShown = useDisclosure();
	const [nfcTag, setNfcTag] = useState("");
	const [copiandoNFC, setCopiandoNFC] = useState(false);

	const onCopyNFC = () => {
		const message = JSON.stringify({
			company: "RIAL",
			email: props.usuario?.email,
			hash: props.usuario?.nfctag,
		});

		if (!("NDEFReader" in window)) {
			if (navigator.clipboard as unknown) {
				navigator.clipboard.writeText(message).then(function () {
					notificacion("Mensaje copiado al portapapeles.");
				}).catch(function (err: unknown) {
					notificacionError("Error al copiar al portapapeles.");
					console.error("Error al copiar al portapapeles: ", err);
					fallbackShown.open();
				});
			} else {
				fallbackShown.open();
			}
		} else {
			setCopiandoNFC(true);
			void copiarNFC(message).then(() => {
				setCopiandoNFC(false);
			});
		}
	};

	useEffect(() => {
		if (props.usuario?.nfctag) {
			setNfcTag(props.usuario.nfctag);
		} else {
			setNfcTag(getTag(24));
		}
	}, [props.usuario]);

	const formRef = useRef<HTMLFormElement>(null);

	return (
		<BaseModal show={props.shown} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Nuevo usuario</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body">
				<UsuarioForm usuario={props.usuario} ref={formRef} nfcTag={nfcTag} close={props.close} key={props.usuario?.id ?? "new"} />
			</div>
			<div className="modal-footer">
				{copiandoNFC ? (
					<button type="button" className="btnPrimary" style={{ float: "left" }} disabled>Copiando, ponga la tarjeta en el lector...</button>
				) : (
					<button type="button" onClick={onCopyNFC} className="btnSecondary" style={{ float: "left" }}>Copiar contenido NFC</button>
				)}
				<button type="button" className="btnPrimary" onClick={() => formRef.current?.requestSubmit()}>Guardar</button>
				<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
			</div>
		</BaseModal>
	);
}

interface UsuarioFormProps {
	readonly usuario: Usuario | null;
	readonly nfcTag: string;
	readonly ref?: React.Ref<HTMLFormElement>;
	readonly close: () => void;
}
function UsuarioForm(props: UsuarioFormProps) {
	const queryClient = useQueryClient();

	const [rol, setRol] = useState("");
	const [showPassword, setShowPassword] = useState(false);

	const { data: operarios, error: errorOperarios, isPending: isPendingOperarios } = UseOperarios();
	const { data: roles, error: errorRoles, isPending: isPendingRoles } = UseRoles();

	useEffect(() => {
		if (props.usuario?.rol) setRol(props.usuario.rol);
	}, [props.usuario]);

	function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		const formValues = Object.fromEntries(formData.entries());

		if (formValues.email == "") {
			return notificacionTipo("error", "Debe insertar un email.");
		}
		if (formValues.id == "" && formValues.password == "") {
			return notificacionTipo("error", "Debe insertar una contraseña.");
		}

		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/anadirusuario", formValues)
			.then((data) => {
				if (data.tipo != "exito") {
					notificacionError(data.message ?? "Error al guardar el usuario.");
					return;
				}

				notificacion(data.message ?? "Usuario guardado con éxito.");
				props.close();
				void queryClient.invalidateQueries({ queryKey: ["getListadoUsuarios"] });
			});
	}

	return (
		<form className="content-box big-box" onSubmit={onSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }} ref={props.ref}>
			<div>
				<input className="inputsForm" type="hidden" name="id" value={props.usuario?.id ?? "new"} />

				<label className="labelsForm">Nombre</label>
				<input className="inputsForm" type="text" name="nombre" defaultValue={props.usuario?.nombre} tabIndex={1} />

				<label className="labelsForm">E-mail</label>
				<input className="inputsForm" type="text" name="email" defaultValue={props.usuario?.email} tabIndex={3} />

				<label className="labelsForm">Telefono</label>
				<input className="inputsForm" type="text" name="telefono" defaultValue={props.usuario?.telefono} tabIndex={5} />

				<div id="divSelectOperario" style={{ display: rol == "operario" ? "" : "none" }}>
					<label className="labelsForm">Perfil Asignado</label>
					{errorOperarios ? (
						<QueryError error={errorOperarios} fallback="Error al cargar los operarios." />
					) : (
						<select className="form-control limpiar" name="operario" tabIndex={6} defaultValue={props.usuario?.operario ?? ""}>
							{isPendingOperarios == true && <option disabled>Cargando operarios...</option>}
							{operarios != null && <>
								<option value="" selected>Elige</option>
								{operarios.map(operario => (
									<option value={operario.idOperario} key={operario.idOperario}>
										{operario.nombreOperario}
									</option>
								))}
							</>}
						</select>
					)}
				</div>
			</div>
			<div>
				<label className="labelsForm">Apellido</label>
				<input className="inputsForm" type="text" name="apellido" tabIndex={2} defaultValue={props.usuario?.apellido} />
				<label className="labelsForm">Contraseña</label>
				<div style={{ position: "relative" }}>
					<input
						className="inputsForm"
						type={showPassword ? "text" : "password"}
						name="password"
						autoComplete="new-password"
						tabIndex={4}
						aria-label="Contraseña"
						style={{ paddingRight: "38px" }}
					/>
					<button
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						aria-pressed={showPassword}
						aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
						style={{
							position: "absolute",
							right: "8px",
							top: "50%",
							transform: "translateY(-50%)",
							background: "none",
							border: "none",
							cursor: "pointer",
							fontSize: "16px",
							padding: 4,
						}}
					>
						<i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
					</button>
				</div>

				<label className="labelsForm">Rol</label>
				{errorRoles ? (
					<QueryError error={errorRoles} fallback="Error al cargar los roles." />
				) : (
					<select className="form-control limpiar" name="rol" tabIndex={7} value={rol} onChange={(e) => setRol(e.target.value)}>
						{isPendingRoles == true && <option disabled>Cargando roles...</option>}
						{roles != null && <>
							<option value="" selected>Elige</option>
							{roles.map(rol => (
								<option key={rol.id} value={rol.id}>
									{rol.nombre}
								</option>
							))}
						</>}
					</select>
				)}
				<label style={{ marginBottom: "2%" }} />
			</div>
			<div id="fallbackCopiar" style={{ display: "none" }}>
				<textarea value={props.nfcTag} name="nfctag" />
			</div>
		</form >
	);
}


function getTag(length: number) {
	let result = "";
	const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	const charactersLength = characters.length;
	let counter = 0;
	while (counter < length) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
		counter += 1;
	}
	return result;
}
async function copiarNFC(message: string) {
	/* eslint-disable @typescript-eslint/no-unsafe-assignment */
	/* eslint-disable @typescript-eslint/no-unsafe-call */
	/* eslint-disable @typescript-eslint/no-unsafe-member-access */
	try {
		console.log("Solicitando permiso NFC...");
		//@ts-ignore -- No esta tipado
		const nfc = new NDEFReader();
		await nfc.scan();

		const encoder = new TextEncoder();
		const messageBuffer: ArrayBuffer = encoder.encode(message).buffer;

		console.log("Escribiendo en la tarjeta NFC...");
		await nfc.write({
			records: [
				{
					recordType: "mime",
					mediaType: "application/json",
					data: messageBuffer,
				}
			],
		});

		notificacion("Información escrita correctamente en la tarjeta NFC.");
	} catch (error) {
		notificacionError("Error al escribir en la tarjeta NFC. Asegúrate de que la tarjeta es compatible.");
		console.error(error);
	}
	/* eslint-enable @typescript-eslint/no-unsafe-assignment */
	/* eslint-enable @typescript-eslint/no-unsafe-call */
	/* eslint-enable @typescript-eslint/no-unsafe-member-access */
}


function UseOperarios() {
	return useQuery({
		queryKey: ["getOperarios"],
		queryFn: () => {
			return ComunicacionAjax.sendAjaxRequest<Operario[]>("GET", "/getOperarios");
		},
	});
}
function UseRoles() {
	return useQuery({
		queryKey: ["getRoles"],
		queryFn: () => {
			return ComunicacionAjax.sendAjaxRequest<Rol[]>("GET", "/getRoles", {});
		},
	});
}
export interface Operario {
	idOperario: number,
	nombreOperario: string;
}
export interface Rol {
	id: number,
	nombre: string;
}